import React from 'react';
import PropTypes from 'prop-types';
import { Button, Stack, Typography } from '@mui/material';
import { CleaningServices, Refresh, Report } from '@mui/icons-material';
import i18next from './services/i18n';
import customHistory from './routes/history';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Determine if this is a chunk load failure
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed\./.test(error.message);

    // Update state to trigger fallback UI
    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for reporting
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Push an "error" flag into history state
    customHistory.replace({
      pathname: customHistory.location.pathname,
      state: { ...(customHistory.location.state || {}), error: true },
    });

    // If it's specifically a chunk load error, trigger a hard reload
    if (this.state.isChunkError) {
      // Allow the UI to update, then reload ignoring cache
      setTimeout(() => {
        window.location.reload(true);
      }, 1000);
    }
  }

  render() {
    if (this.state.hasError) {
      // If it was a chunk error, show an updating message
      if (this.state.isChunkError) {
        return (
          <Stack
            sx={{ padding: 2, backgroundColor: '#09152D' }}
            justifyContent="center"
            alignItems="center"
            height="100vh"
          >
            <Report sx={{ fontSize: 120, color: 'white' }} />
            <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
              {i18next.t('error_boundary.updating')}
            </Typography>
            <Typography sx={{ color: 'white' }}>
              {i18next.t('error_boundary.please_wait')}
            </Typography>
          </Stack>
        );
      }

      // Generic fallback UI for all other errors
      return (
        <Stack
          sx={{ padding: 2, backgroundColor: '#09152D' }}
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <Report sx={{ fontSize: 120, color: 'white' }} />
          <Typography variant="h3" sx={{ color: 'white' }}>
            {i18next.t('error_boundary.title')}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button onClick={() => window.location.reload()} size="large">
              <Refresh sx={{ mr: 1 }} fontSize="large" />
              {i18next.t('error_boundary.reload')}
            </Button>
            <Button
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }}
              size="large"
            >
              <CleaningServices sx={{ mr: 1 }} fontSize="large" />
              {i18next.t('error_boundary.clear_and_reload')}
            </Button>
          </Stack>
        </Stack>
      );
    }

    // Render children when there's no error
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.any,
};

export default ErrorBoundary;
